<script setup>
import { computed } from 'vue';
import { useI18n } from 'vue-i18n';
import TaskCardContent from './TaskCardContent.vue';
import { isDone } from './taskHelpers.js';
import { useTasksStore } from './useTasksStore.js';

const props = defineProps({
  task: {
    type: Object,
    required: true,
  },
  compact: {
    type: Boolean,
    default: false,
  },
});

const emit = defineEmits(['open', 'complete']);

const { t } = useI18n();
const tasksStore = useTasksStore();

const contact = computed(() =>
  tasksStore.findContact(props.task.linkedContactId)
);
const project = computed(() =>
  tasksStore.findProject(props.task.linkedProjectId)
);
const isCompleted = computed(() => isDone(props.task));

const contactName = computed(
  () => contact.value?.name || t('TASKS.EMPTY_LINKS.CONTACT_UNAVAILABLE')
);

const projectTitle = computed(() =>
  props.task.linkedProjectId
    ? project.value?.title || t('TASKS.EMPTY_LINKS.PROJECT_UNAVAILABLE')
    : ''
);

const nextAction = computed(() => {
  if (props.task.type === 'follow_up') return t('TASKS.TYPE.FOLLOW_UP');
  if (props.task.type === 'call') return t('TASKS.TYPE.CALL');
  if (props.task.type === 'whatsapp') return t('TASKS.TYPE.WHATSAPP');
  if (props.task.type === 'email') return t('TASKS.TYPE.EMAIL');
  if (props.task.type === 'appointment') return t('TASKS.TYPE.APPOINTMENT');
  if (props.task.type === 'stock_check') return t('TASKS.TYPE.STOCK_CHECK');
  if (props.task.type === 'purchase_project') {
    return t('TASKS.TYPE.PURCHASE_PROJECT');
  }
  if (props.task.type === 'internal_note') return t('TASKS.TYPE.INTERNAL_NOTE');
  if (props.task.type === 'other') return t('TASKS.TYPE.OTHER');
  return '';
});

const completionClasses = computed(() => {
  if (isCompleted.value) {
    return 'border-n-teal-8 bg-n-teal-9 text-white';
  }

  return 'border-n-slate-7 bg-white text-transparent group-hover:border-n-slate-9';
});

const complete = event => {
  event.stopPropagation();
  emit('complete', props.task);
};
</script>

<template>
  <div
    role="button"
    tabindex="0"
    class="group flex w-full gap-3 bg-n-solid-1 px-4 py-3 text-left transition-colors hover:bg-n-alpha-1"
    :class="{ 'opacity-55': isCompleted }"
    @click="emit('open', task)"
    @keydown.enter="emit('open', task)"
    @keydown.space.prevent="emit('open', task)"
  >
    <button
      type="button"
      class="mt-0.5 box-border flex h-5 min-h-5 w-5 min-w-5 shrink-0 items-center justify-center rounded-full border-2 border-solid p-0 transition-transform active:scale-95"
      :class="completionClasses"
      :title="t('TASKS.ACTIONS.MARK_DONE')"
      @click="complete"
    >
      <span v-if="isCompleted" class="i-lucide-check size-3" />
    </button>

    <TaskCardContent
      :task="task"
      :compact="compact"
      :contact-name="contactName"
      :project-title="projectTitle"
      :type-label="nextAction"
      :completed="isCompleted"
    />
  </div>
</template>
